export const THUMBNAIL_SIZE = 140;

const OVERLAY_SIZE = 20;
const OVERLAY_INSET = 4;
const SVG_CONTENT_TYPE = 'image/svg+xml';

// Ids applied by the recolour pipelines; their presence means the asset is colour changeable.
const RECOLOURABLE_SELECTOR = '#vs-primary, #vs-secondary, #vs-skin, #vs-hair, #vs-highlight, #vs-outline';

const getColourChangeOverlaySVG = (
  size: number
) => `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="20" height="20" fill="none"/>
    <path d="M2.5 5H11.875M17.5 5H15.625" stroke="#E6553B" stroke-width="1.5625" stroke-linecap="round"/>
    <circle cx="13.75" cy="5" r="1.71875" stroke="#E6553B" stroke-width="1.5625"/>
    <path d="M2.5 15H11.875M17.5 15H15.625" stroke="#4FA4CD" stroke-width="1.5625" stroke-linecap="round"/>
    <circle cx="13.75" cy="15" r="1.71875" stroke="#4FA4CD" stroke-width="1.5625"/>
    <path d="M17.5 10L8.125 10M2.5 10L4.375 10" stroke="#71CA51" stroke-width="1.5625" stroke-linecap="round"/>
    <circle cx="6.25" cy="10" r="1.71875" transform="rotate(-180 6.25 10)" stroke="#71CA51" stroke-width="1.5625"/>
</svg>`;

function loadImage(source: Blob | string): Promise<HTMLImageElement> {
  const objectUrl = typeof source === 'string' ? source : URL.createObjectURL(source);

  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image for thumbnail generation'));
    img.src = objectUrl;
  }).finally(() => {
    if (typeof source !== 'string') URL.revokeObjectURL(objectUrl);
  });
}

function getSvgIntrinsicSize(root: SVGSVGElement): { width: number; height: number } | null {
  const width = parseFloat(root.getAttribute('width') || '');
  const height = parseFloat(root.getAttribute('height') || '');
  if (width > 0 && height > 0) return { width, height };

  const viewBox = (root.getAttribute('viewBox') || '').split(/[\s,]+/).map(Number);
  if (viewBox.length === 4 && viewBox[2] > 0 && viewBox[3] > 0) {
    return { width: viewBox[2], height: viewBox[3] };
  }

  return null;
}

type ThumbnailSource = {
  image: HTMLImageElement;
  width: number;
  height: number;
  isColorChangeable: boolean;
};

async function loadSvgSource(file: Blob, size: number): Promise<ThumbnailSource> {
  const svgText = await file.text();
  const root = new DOMParser().parseFromString(svgText, SVG_CONTENT_TYPE).documentElement as unknown as SVGSVGElement;

  const isColorChangeable =
    !!root.querySelector(RECOLOURABLE_SELECTOR) || root.getAttribute('data-provider') === 'noun-project';

  const intrinsic = getSvgIntrinsicSize(root) ?? { width: size, height: size };
  const scale = size / Math.max(intrinsic.width, intrinsic.height);
  const width = Math.max(1, Math.round(intrinsic.width * scale));
  const height = Math.max(1, Math.round(intrinsic.height * scale));

  if (!root.getAttribute('viewBox')) {
    root.setAttribute('viewBox', `0 0 ${intrinsic.width} ${intrinsic.height}`);
  }
  root.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  root.setAttribute('width', String(width));
  root.setAttribute('height', String(height));

  const image = await loadImage(new Blob([root.outerHTML], { type: SVG_CONTENT_TYPE }));

  return { image, width, height, isColorChangeable };
}

async function loadRasterSource(file: Blob, size: number): Promise<ThumbnailSource> {
  const image = await loadImage(file);
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;

  if (!sourceWidth || !sourceHeight) throw new Error('Image has no intrinsic dimensions');

  const scale = Math.min(size / Math.max(sourceWidth, sourceHeight), 1);

  return {
    image,
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale)),
    isColorChangeable: false
  };
}

/**
 * Renders a PNG thumbnail for an image asset, preserving aspect ratio and transparency.
 * Returns null when the asset isn't an image or can't be rendered, so callers can leave
 * thumbnail generation to the backend.
 */
export async function generateAssetThumbnail(file: Blob, size = THUMBNAIL_SIZE): Promise<Blob | null> {
  if (!file.type.startsWith('image/')) return null;

  try {
    const source =
      file.type === SVG_CONTENT_TYPE ? await loadSvgSource(file, size) : await loadRasterSource(file, size);

    const canvas = document.createElement('canvas');
    canvas.width = source.width;
    canvas.height = source.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source.image, 0, 0, source.width, source.height);

    if (source.isColorChangeable) {
      const overlay = await loadImage(new Blob([getColourChangeOverlaySVG(OVERLAY_SIZE)], { type: SVG_CONTENT_TYPE }));
      ctx.drawImage(overlay, source.width - OVERLAY_SIZE - OVERLAY_INSET, OVERLAY_INSET, OVERLAY_SIZE, OVERLAY_SIZE);
    }

    return await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
  } catch (error) {
    console.warn('Falling back to server-side thumbnail generation', error);
    return null;
  }
}
